import React, { useReducer, useState, useEffect, useContext, useRef } from 'react'
import * as d3 from 'd3';
import { Icon, Dropdown } from 'semantic-ui-react';

import ThemeContext from '@/theme/context'

import { WinnersContainer, WinnerPopup, ControlButtons } from './style'

const isInViewport = (el) => {
    if (!el) return false;
    const rect = el.getBoundingClientRect();
    return rect.bottom >= 0 && rect.top <= (window.innerHeight || document.documentElement.clientHeight);
}

export const Winners = ({ winners, currentAct }) => {
    if (!winners || !winners.length) return null

    const isMobile = window.innerWidth < 768;
    const numNodes = (currentAct === 'fieb') ? (isMobile ? 14 : 20) : (isMobile ? 7 : 11);
    const height = isMobile ? 600 : 400;
    const maxPage = Math.ceil(winners.length / numNodes)
    const { primaryColor } = useContext(ThemeContext);
    const [open, setOpen] = useState<boolean|number>(false)
    const [page, setPage] = useState(Math.floor(Math.random() * maxPage))
    const [updateId, forceUpdate] = useReducer(x => x + 1, 0);
    const svgRef = useRef<any>(null)

    const getNextPage = page => (page + 1) >= maxPage ? 0 : page + 1
    const getPrevPage = page => (page - 1) < 0 ? maxPage - 1 : page - 1

    const getWinners = (page) => {
        if ((page + 1) === maxPage)
            return winners.slice(-numNodes)

        return winners.slice(page * numNodes, page * numNodes + numNodes)
    }

    const pageWinners = getWinners(page)

    useEffect(() => {
        const resetOpen = () => setOpen(false)
        var mql = window.matchMedia("(orientation: portrait)");

        //зактрытие popover при скролле
        isMobile && document.addEventListener('scroll', resetOpen)
        //перерендер компонента при смене ориентации экрана
        mql.addEventListener('change', forceUpdate)

        return () => {
            isMobile && document.removeEventListener('scroll', resetOpen)
            mql.removeEventListener('change', forceUpdate)
        }
    },[])

    useEffect(() => {
        setOpen(false) //зактрытие popover при смене страницы

        if (maxPage <= 1) return;
        const swipeTime = 60000

        const timer = setTimeout(() => {
            //отключил смену страниц если призеры не в области видимости
            if (isInViewport(svgRef.current))
                setPage(page => getNextPage(page))
            else {
                const interval = setInterval(() => {
                    if (isInViewport(svgRef.current)) {
                        setPage(page => getNextPage(page))
                        clearInterval(interval)
                    }
                }, swipeTime);
            }
        }, swipeTime);

        return () => clearTimeout(timer)
    }, [page, updateId]);

    const getWinner = (winner, width) => {
        let scale = 1,
            radius = 35

        if (width < 707) {
            scale = (width + height) * 1.55/(1095 + height)
        } else if (width < 909) {
            scale = .85
        } else if ( width < 1095) {
            scale = .95
        }

        if (currentAct === 'fieb') {
            scale = scale * .75
        }

        switch (winner.rewardStatus) {
            case 'gold':
                radius = 75 * scale
                break;
            case 'silver':
                radius = 70 * scale
                break;
            case 'bronze':
                radius = 65 * scale
                break;
            default:
                break;
        }

        return {
            radius,
            url: '/sites/default/files/winners/' + winner.image_link
        }
    }

    useEffect(() => {
        const width = svgRef.current.clientWidth || svgRef.current.getBoundingClientRect().width
        const nextPage = getNextPage(page)

        const nodes = pageWinners.map(item => getWinner(item, width))

        //для предварительной подгрузки аватаров призеров со следующей страницы (возможно надо добавить и из предыдущей)
        const additionalNodes = getWinners(nextPage).map(item => getWinner(item, width))

        const handleDragStart = (e, d) => {
            if (!e.active) simulation.alpha(.03).restart();
            d.fx = d.x;
            d.fy = d.y;
        }
        const handleDrag = (e, d) => {
            d.fx = e.x;
            d.fy = e.y;
        }
        const handleDragEnd = (e, d) => {
            if (!e.active) simulation.alpha(.03);
            d.fx = null;
            d.fy = null;
        }

        const ticked = () => {
            d3.select('#winners g')
                .selectAll('circle')
                .data(nodes)
                .join('circle')
                .attr('id', d => `circle${d.index}`)
                .attr('r', d => d.radius)
                .attr("fill", d => `url(#pattern${d.index})`)
                .attr("stroke", primaryColor)
                .attr('cx', d => {
                    //ограничение по ширине svg
                    return (d.x = Math.max(d.radius * 1.1 + 1, Math.min(width - d.radius * 1.1 - 1, d.x)));
                })
                .attr('cy', d => {
                    return (d.y = Math.max(d.radius * 1.1 + 1, Math.min(height - d.radius * 1.1 - 1, d.y)));
                })
                .on('mouseover', (e, d) => setOpen(d.index))
                .on('mouseout', () => setOpen(false))
                .call(
                    !isMobile ? 
                        d3.drag()
                        .on("start", handleDragStart)
                        .on("drag", handleDrag)
                        .on("end", handleDragEnd) 
                    : 
                        ()=>{}
                );
        }

        d3.selectAll('#winners g defs').remove();

        let simulation = d3.forceSimulation(nodes)
            .force("center", d3.forceCenter(width / 2, height / 2))
            .force('charge', d3.forceManyBody().strength(1))
            .force('collision', d3.forceCollide().radius(d => {
                return currentAct === 'fieb' ? //элемент рандома чтобы исключить одинаковое расположение кружков в фиэбе
                    d.radius + 7 + Math.floor(Math.random() * 10)
                :
                    d.radius + 10
            }))
            .on('tick', ticked);

        isMobile ?
            simulation.force('x', d3.forceX().x(0))
        :
            simulation.force('y', d3.forceY().y(0))

        //add defs
        d3.select('#winners g')
            .append('defs')
            .selectAll("pattern")
            .data(nodes.concat(additionalNodes))
            .enter()
            .append("pattern")
            .attr("id", d => `pattern${d.index}`)
            .attr('height', '100%')
            .attr('width', '100%')
            .append('image')
            .attr('height', d => Math.round(d.radius) * 2)
            .attr('width', d => Math.round(d.radius) * 2)
            .attr("xmlns:xlink", "http://www.w3.org/1999/xlink")
            .attr("xlink:href", d => d.url)
            .attr("preserveAspectRatio", 'xMidYMid slice')
    }, [page, updateId]);

    return (<WinnersContainer>
        <svg id="winners" width="100%" height={height} ref={svgRef}>
            <g/>
        </svg>
        {maxPage > 1 && <ControlButtons>
            <Icon name="angle left" onClick={() => setPage(page => getPrevPage(page))}/>
            <Icon name="angle right" onClick={() => setPage(page => getNextPage(page))} />
        </ControlButtons>}
        {pageWinners.map((winner, i) =>
            <WinnerPopup
                key={i}
                context={document.getElementById('circle' + i) || undefined}
                content={<>
                    {(currentAct === 'olymp' || currentAct === 'olymp_en') && <div className='reward'>
                        <Icon name="star" circular /><span>{winner.reward}</span>
                    </div>}
                    {winner.spec && <>
                        <div className='spec'>{winner.spec}</div>
                    </>}
                    <div><b>{winner.fio}</b></div>
                    <div className='vuz'>{winner.vuz}</div>
                </>}
                position={isMobile ? undefined : 'top center'}
                open={open === i}
                wide={true}
                $borderColor={primaryColor}
                $rewardColor={winner.rewardStatus === 'gold' ? '#f8b411' : winner.rewardStatus === 'silver' ? '#bcc0c1' : '#fe7900'}
            />
        )}
    </WinnersContainer>)
}
